import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone, Clock } from "lucide-react";
import { Reveal, ScriptKicker, FilipinoSun } from "../components/Reveal";
import { BUSINESS } from "../lib/data";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Wedding", "Birthday", "Corporate Event", "Graduation", "Family Gathering", "Community Celebration", "Other"];

export default function Contact() {
  usePageMeta({
    title: "Book Filipino Catering in San Diego | Ateng K Sarap",
    description:
      "Ready to book? Contact Ateng K Sarap for Filipino catering quotes on birthdays, weddings, corporate events & private parties in San Diego.",
    path: "/contact",
  });
  const [tab, setTab] = useState("catering");

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24 bg-background">
        <div className="absolute top-16 right-10 text-accent/25 hidden lg:block"><FilipinoSun size={140} /></div>
        <div className="container-eds relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> Contact Us</div>
            <ScriptKicker className="block mt-3">Let's plan something delicious</ScriptKicker>
            <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-foreground leading-[0.98] tracking-tight">
              Let's plan your Filipino <span className="italic text-secondary">feast</span>.
            </h1>
          </div>
          <div className="lg:col-span-4 text-muted-foreground text-base lg:text-lg">
            Reach out by phone, email, or the form below. We respond within 24 hours.
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-background">
        <div className="container-eds grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <div className="overline">Visit & Reserve</div>
            <ScriptKicker className="block mt-2">Come, let's chat</ScriptKicker>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl font-medium text-foreground">
              The <span className="italic text-primary">kitchen</span> behind the warmth.
            </h2>
            <ul className="mt-10 space-y-7">
              <InfoRow icon={MapPin} label="Address">{BUSINESS.address}</InfoRow>
              <InfoRow icon={Phone} label="Phone">
                <a href={BUSINESS.phoneLink} data-testid="contact-phone-link" className="hover:text-secondary transition-colors">{BUSINESS.phone}</a>
              </InfoRow>
              <InfoRow icon={Mail} label="Email">
                <a href={BUSINESS.emailLink} data-testid="contact-email-link" className="hover:text-secondary transition-colors break-all">{BUSINESS.email}</a>
              </InfoRow>
              <InfoRow icon={Clock} label="Booking Notice">Orders must be placed one week prior to your event.</InfoRow>
            </ul>
            <a href={BUSINESS.phoneLink} data-testid="contact-click-to-call-btn" className="btn-primary mt-10">
              <Phone className="w-4 h-4" /> Click to Call
            </a>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="bg-card border border-border rounded-3xl overflow-hidden">
              <div className="flex border-b border-border">
                <button type="button" data-testid="tab-catering-inquiry" onClick={() => setTab("catering")}
                        className={`flex-1 py-5 text-xs sm:text-sm tracking-wide uppercase font-bold transition-colors ${tab === "catering" ? "bg-secondary text-secondary-foreground" : "text-foreground hover:bg-muted"}`}>
                  Catering Inquiry
                </button>
                <button type="button" data-testid="tab-general-message" onClick={() => setTab("general")}
                        className={`flex-1 py-5 text-xs sm:text-sm tracking-wide uppercase font-bold transition-colors ${tab === "general" ? "bg-secondary text-secondary-foreground" : "text-foreground hover:bg-muted"}`}>
                  General Message
                </button>
              </div>
              <div className="p-8 lg:p-10">
                {tab === "catering" ? <CateringForm /> : <GeneralForm />}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-card">
        <div className="container-eds py-16 lg:py-20">
          <Reveal>
            <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> Find Us</div>
            <ScriptKicker className="block mt-2">Visit San Diego</ScriptKicker>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl font-medium text-foreground leading-tight max-w-2xl">
              2707 Boston Avenue, <span className="italic text-primary">San Diego</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 border border-border rounded-3xl overflow-hidden">
              <iframe
                title="Ateng K Sarap Location"
                src={BUSINESS.mapEmbed}
                data-testid="contact-map"
                width="100%"
                height="450"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          padding: 0.85rem 1.1rem;
          font-size: 0.95rem;
          color: hsl(var(--foreground));
          border-radius: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-input:focus {
          border-color: hsl(var(--secondary));
          box-shadow: 0 0 0 3px hsl(var(--secondary) / 0.2);
        }
      `}</style>
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <li className="flex gap-5">
      <div className="w-12 h-12 rounded-full shrink-0 bg-primary text-primary-foreground flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-bold mb-1">{label}</div>
        <div className="text-foreground text-base">{children}</div>
      </div>
    </li>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold mb-2">{label}</span>
      {children}
    </label>
  );
}

function CateringForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "Wedding",
    event_date: "", guest_count: "", budget: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/catering-inquiry`, form);
      toast.success("Thank you! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", event_type: "Wedding", event_date: "", guest_count: "", budget: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="catering-inquiry-form" onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full Name *"><input data-testid="cater-name-input" name="name" value={form.name} onChange={onChange} required className="form-input" /></Field>
        <Field label="Phone *"><input data-testid="cater-phone-input" name="phone" type="tel" value={form.phone} onChange={onChange} required className="form-input" /></Field>
        <Field label="Email *"><input data-testid="cater-email-input" name="email" type="email" value={form.email} onChange={onChange} required className="form-input" /></Field>
        <Field label="Event Type">
          <select data-testid="cater-event-type-select" name="event_type" value={form.event_type} onChange={onChange} className="form-input">
            {EVENT_TYPES.map((e) => <option key={e}>{e}</option>)}
          </select>
        </Field>
        <Field label="Event Date"><input data-testid="cater-event-date-input" name="event_date" type="date" value={form.event_date} onChange={onChange} className="form-input" /></Field>
        <Field label="Guest Count"><input data-testid="cater-guest-count-input" name="guest_count" value={form.guest_count} onChange={onChange} placeholder="e.g. 50" className="form-input" /></Field>
        <Field label="Approx Budget"><input data-testid="cater-budget-input" name="budget" value={form.budget} onChange={onChange} placeholder="e.g. $1,500" className="form-input" /></Field>
      </div>
      <Field label="Tell us about your event">
        <textarea data-testid="cater-message-input" name="message" value={form.message} onChange={onChange} rows={4} className="form-input resize-none" placeholder="Dishes you'd love, dietary needs, venue, theme…" />
      </Field>
      <button type="submit" data-testid="cater-submit-btn" disabled={loading} className="btn-secondary w-full sm:w-auto disabled:opacity-60">
        {loading ? "Sending..." : "Send Catering Inquiry"} <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-xs text-muted-foreground italic font-script text-base">* Orders must be placed one week prior to your event.</p>
    </form>
  );
}

function GeneralForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="general-contact-form" onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Name *"><input data-testid="gen-name-input" name="name" value={form.name} onChange={onChange} required className="form-input" /></Field>
        <Field label="Email *"><input data-testid="gen-email-input" name="email" type="email" value={form.email} onChange={onChange} required className="form-input" /></Field>
        <Field label="Phone"><input data-testid="gen-phone-input" name="phone" value={form.phone} onChange={onChange} className="form-input" /></Field>
        <Field label="Subject *"><input data-testid="gen-subject-input" name="subject" value={form.subject} onChange={onChange} required className="form-input" /></Field>
      </div>
      <Field label="Message *">
        <textarea data-testid="gen-message-input" name="message" value={form.message} onChange={onChange} required rows={5} className="form-input resize-none" />
      </Field>
      <button type="submit" data-testid="gen-submit-btn" disabled={loading} className="btn-secondary w-full sm:w-auto disabled:opacity-60">
        {loading ? "Sending..." : "Send Message"} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}
